//-- Copyright (c) 2021 TMT Insights Group, LLC
import React, { useEffect } from 'react';
import { ColumnConfig } from '@/__configs/config_types';
import { useTheme } from '@mui/material/styles';
import {
  selectCurrentTableKey,
  selectDataTableFilters,
  selectDataTableResults,
  selectExpandedRowIds,
  selectRowEditedId
} from '@/redux/selectors/dataTableSelectors';
import { collapseRow, expandRow } from '@/redux/slices/dataTableSlice';
import { TableColumn } from '@/redux/slices/slice_types';
import styled from '@emotion/styled';
import { Fade, FadeProps, TableRow as MuiTableRow, TableRowProps as MuiTableRowProps, TableCell, darken, lighten } from '@mui/material';
import { insertWordBreaks } from '@/_utils/utils';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { selectHasOrderCategories } from '@/redux/selectors/constantsSelectors';
import { useSortable } from '@dnd-kit/react/sortable';
import TableDragHandle from './TableDragHandle';
import { TableExpandCell } from './TableExpandCell';
import { DroppableType } from './TableDnd';
import ClickCopy from '../ClickCopy/ClickCopy';

type TableRowProps = MuiTableRowProps & {
  columns: TableColumn[];
  onRowClick?: React.MouseEventHandler<HTMLTableRowElement>;
  isActiveRow?: boolean;
  item: any;
  index: number;
  scrollable: boolean;
  scrollEnd: 'left' | 'right' | null;
  group?: string;
  className?: string;
  draggingGroup?: string | null;
  disableDrag?: boolean;
};

const END_STATES = [
  'accepted',
  'cancelled',
  'complete',
  'complete_with_errors',
  'deferred',
  'omitted',
  'referred',
  'rejected',
  'resolved',
  'skipped'
];

const TableRow = ({
  draggingGroup,
  disableDrag,
  group,
  columns,
  onRowClick,
  isActiveRow,
  item,
  index,
  scrollable,
  scrollEnd,
  className,
  ...props
}: TableRowProps) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const expandedRowIds = useAppSelector(selectExpandedRowIds);
  const isCollapsed = !expandedRowIds.includes(item.id);
  const filters = useAppSelector(selectDataTableFilters);
  const totalResults = useAppSelector(selectDataTableResults);
  const isEdit = useAppSelector(selectRowEditedId) === item.id;
  const tableName = useAppSelector(selectCurrentTableKey);
  const hasCategories = useAppSelector(selectHasOrderCategories);
  const ExpandableRow = columns.find((col) => col.id === 'expandable');
  const filteredColumns = columns.filter((col) => col.id !== 'expandable' && !col.omitted);
  const disabled = disableDrag || END_STATES.includes(item.status);
  const { ref, isDragSource } = useSortable({
    index,
    id: item.id,
    data: { group },
    disabled: disableDrag
  });

  useEffect(() => {
    if (filters?.search?.[0] === 'W' && totalResults === 1) {
      dispatch(expandRow({ rowId: item.id }));
    }
  }, [filters.search, totalResults]);

  const formatCellData = (cell: ColumnConfig<any, any>) => {
    const dataIndex = item;
    let cellData;
    if (!dataIndex) return;

    // TODO: this should be changed to use the key and split by __ (to match BE)
    // and eliminate the 'field' property
    if (cell.field?.includes('__')) {
      cellData = cell.field.split('__').reduce((o: any, i: string) => o?.[i], dataIndex);
    } else {
      cellData = dataIndex?.[cell.field];
    }

    // if we aren't receiving dragHandleProps, the dnd condition has not been met
    // draggable is a function that returns a boolean in the column definition
    if (cell.draggable && !disabled) {
      return <TableDragHandle priority={cellData} isDraggable={!disabled && cell.draggable(cellData, item)} />;
    } else if (cell.render) {
      cellData = cell.render(cellData, item);
    }

    if (typeof cellData === 'string') {
      cellData = insertWordBreaks(cellData);
    }

    return cellData || '-';
  };

  const toggleCollapse = () => {
    if (isCollapsed) {
      dispatch(expandRow({ rowId: item.id }));
    } else {
      dispatch(collapseRow({ rowId: item.id }));
    }
  };

  const expandDisabled =
    (tableName === 'orders' && item.status === 'draft') ||
    tableName === 'focusAvails' ||
    tableName === 'focusReports' ||
    ExpandableRow?.disableExpand?.(item);

  const getRowStyle = (isHidden: DroppableType['hidden']) => {
    const styles: any = {
      opacity: isHidden ? 0.5 : 1,
      maxHeight: isHidden && !draggingGroup ? '0 !important' : 'auto'
    };
    if (isDragSource && draggingGroup === 'priority' && group === 'nonPriority') {
      styles.outline = `1px solid ${theme.palette.error.dark}8a`;
    }
    if (isDragSource) {
      delete styles.opacity;
    }
    return styles;
  };

  const ConditionalFade = !disabled ? FadeFragment : Fade;

  return (
    <>
      <ConditionalFade in timeout={index ? (Math.min(index, 30) + 1) * 100 : 100}>
        <StyledRow
          className={`${className || ''} table-row`}
          clickable={+(!expandDisabled || !!onRowClick)}
          active={+!!isActiveRow}
          onClick={isEdit ? undefined : onRowClick || toggleCollapse}
          {...props}
          style={getRowStyle(item.hidden)}
          ref={ref}
        >
          {tableName === 'orders' ? (
            ExpandableRow && !expandDisabled ? (
              <TableExpandCell scrollable={scrollable} scrollEnd={scrollEnd} isCollapsed={isCollapsed} setIsCollapsed={toggleCollapse} />
            ) : (
              <TableCell className="expandable">
                {scrollable && scrollEnd !== 'left' && <Overlay className={'overlay-right'} position="right" />}
              </TableCell>
            )
          ) : (
            ExpandableRow && (
              <TableExpandCell
                scrollable={scrollable}
                scrollEnd={scrollEnd}
                isCollapsed={isCollapsed}
                setIsCollapsed={toggleCollapse}
                disabled={expandDisabled}
              />
            )
          )}
          {filteredColumns.map((cell, j) => {
            if (!cell.hidden) {
              return (
                <TableCell
                  key={`cell-${j}`}
                  className={cell.id === 'overflow' ? 'overflow' : ''}
                  data-testid={cell.id === 'overflow' ? undefined : cell.id?.replace(/_/g, '-')}
                  style={{
                    minWidth: cell.minWidth,
                    maxWidth: cell.maxWidth,
                    width: cell.width,
                    position: cell.id === 'priority' && scrollable && scrollEnd !== 'left' ? 'sticky' : undefined
                  }}
                >
                  {cell.id === 'priority' && scrollable && scrollEnd !== 'left' && <Overlay className={'overlay-right'} position="right" />}
                  {cell.id === 'overflow' && scrollable && scrollEnd !== 'right' && <Overlay className={'overlay-left'} position="left" />}
                  {cell.clickCopy ? (
                    <ClickCopy noBackground disableTextClick>
                      {formatCellData(cell)}
                    </ClickCopy>
                  ) : (
                    formatCellData(cell)
                  )}
                </TableCell>
              );
            }
          })}
        </StyledRow>
      </ConditionalFade>

      {ExpandableRow && !expandDisabled && (
        <ExpandableStyledRow active={+!!isActiveRow} collapsed={+isCollapsed}>
          <TableCell colSpan={columns.length} key="cell-expandable">
            {ExpandableRow.render &&
              (ExpandableRow.render as (props: { item: any; isCollapsed: boolean; hasCategories: boolean }) => React.ReactNode)({
                isCollapsed,
                item,
                hasCategories
              })}
          </TableCell>
        </ExpandableStyledRow>
      )}
    </>
  );
};

const FadeFragment = ({ children }: FadeProps) => {
  return <>{children}</>;
};

export default TableRow;

export const Overlay = styled.span<{ position: 'left' | 'right'; header?: boolean }>`
  position: absolute;
  width: 40px;
  height: 100%;
  top: 0;
  ${({ position }) => position}: -40px;
  background: ${({ theme, header, position }) =>
    position === 'left'
      ? `linear-gradient(to left, ${header ? theme.palette.background.default : theme.palette.background.paper}, transparent 75%)`
      : `linear-gradient(to left, transparent 25%, ${header ? theme.palette.background.default : theme.palette.background.paper})`};
`;

const StyledRow = styled(MuiTableRow)<{ clickable: number; active: number }>`
  cursor: ${({ clickable }) => (clickable ? 'pointer' : 'default')};
  background: ${({ theme }) => theme.palette.background.paper};

  &:hover {
    td:first-of-type {
      background: ${({ theme }) =>
        theme.palette.mode === 'light' ? darken(theme.palette.background.paper, 0.03) : lighten(theme.palette.background.paper, 0.03)};
    }

    background: ${({ theme }) =>
      theme.palette.mode === 'light' ? darken(theme.palette.background.paper, 0.03) : lighten(theme.palette.background.paper, 0.03)};

    .overflow,
    .expandable {
      background: ${({ theme }) =>
        theme.palette.mode === 'light' ? darken(theme.palette.background.paper, 0.03) : lighten(theme.palette.background.paper, 0.03)} !important;
    }

    .overlay-right {
      background: linear-gradient(
        to left,
        transparent 25%,
        ${({ theme }) =>
          theme.palette.mode === 'light' ? darken(theme.palette.background.paper, 0.03) : lighten(theme.palette.background.paper, 0.03)}
      );
    }

    .overlay-left {
      background: linear-gradient(
        to left,
        ${({ theme }) =>
          theme.palette.mode === 'light' ? darken(theme.palette.background.paper, 0.03) : lighten(theme.palette.background.paper, 0.03)},
        transparent 75%
      );
    }
  }

  ${({ active, theme }) =>
    active &&
    `
    background: ${lighten(theme.palette.background.paper, 0.1)};
    border-left: 3px solid ${theme.palette.primary.main};
  `}

  & td > strong {
    word-break: break-word;
  }
`;

const ExpandableStyledRow = styled(MuiTableRow)<{ active: number; collapsed: number }>`
  background: ${({ theme }) => theme.palette.background.paper};

  ${({ active, theme }) =>
    active &&
    `
  background: ${lighten(theme.palette.background.paper, 0.1)};
  border-left: 3px solid ${theme.palette.primary.main};
`}

  & td > strong {
    word-break: break-word;
  }

  & td {
    border-width: ${({ collapsed }) => (collapsed ? 0 : 1)};
  }

  ${({ collapsed, theme }) =>
    collapsed
      ? `
    & td {
      padding: 0;
    }
    `
      : `
    overflow: hidden;
    border-left: 5px solid ${theme.palette.primary.main};
    margin-top: 0;
    `}
`;

type TableRowHiddenProps = {
  children: React.ReactNode;
  hidden?: boolean;
  group: 'priority' | 'nonPriority';
};

export const TableRowHidden = ({ children, hidden = true, group }: TableRowHiddenProps) => {
  const { ref } = useSortable({
    index: 0,
    id: `placeholder-${group}`,
    data: { group }
  });

  return (
    <StyledTableRow hidden={hidden} className="row-hidden" ref={ref}>
      {children}
    </StyledTableRow>
  );
};

const StyledTableRow = styled.tr<{ hidden?: boolean }>`
  opacity: 0;

  ${(props) =>
    !props.hidden &&
    `
    opacity: 1;
  `};

  td {
    background: ${(props) => props.theme?.palette?.warning?.main}1a;
    opacity: 1;
    font-size: 1.2rem;
    max-height: unset;
    line-height: 1.2rem;
    transition: all 0.5s ease;
    & svg {
      transition: ease all 0.5s;
      min-height: 30px;
    }

    & > div {
      transition: ease all 0.5s;
      padding: ${(props) => props.theme.spacing(3)};
    }
  }
`;
